/**
 * v2.0.0 question bank — code mirror of docs/02-product/question-bank-v2.md.
 *
 * This is the CURRENT bank. v1 is archived in question-bank-v1.ts.
 *
 * Sprint 6 (ADR-014): expands the bank from 5 → 7 dimensions by adding
 * `planning` (Anchor) and `growth` (Investor). 14 v1 items are carried over
 * verbatim; 6 v1 items are dropped (imp_02, sec_03, sca_04, avo_03, idn_02,
 * idn_03) and 6 new items are added (pln_01/02/03, grw_01/02/03).
 *
 * DO NOT edit values here without updating the doc first
 * (docs are the source of truth). The drift test in `question-bank.test.ts`
 * enforces the doc/code lock.
 */

export const BANK_VERSION_V2 = "v2.0.0" as const;
export const SCORE_VERSION_V2 = "v2.0.0" as const;
export const SCORE_VERSION_INT_V2 = 2;
export const CONTENT_VERSION_V2 = "v2.0.0" as const;

export const DIMENSIONS = [
  "impulse",
  "security",
  "scarcity",
  "avoidance",
  "identity",
  "planning",
  "growth",
] as const;
export type Dimension = (typeof DIMENSIONS)[number];

/**
 * v2 dimension → archetype map (7 archetypes). planning → anchor and
 * growth → investor are the Sprint 6 additions (ADR-014).
 */
export const DIMENSION_TO_ARCHETYPE = {
  impulse: "spender",
  security: "saver",
  scarcity: "hoarder",
  avoidance: "avoider",
  identity: "shopper",
  planning: "anchor",
  growth: "investor",
} as const satisfies Record<Dimension, string>;

export type QuestionType = "likert" | "scenario" | "frequency";

export interface AnswerOption {
  /** Raw value as the user picks it. */
  value: number;
  label: string;
}

export interface Question {
  id: string;
  dim: Dimension;
  type: QuestionType;
  weight: number;
  text: string;
  answers: ReadonlyArray<AnswerOption>;
}

const LIKERT: ReadonlyArray<AnswerOption> = [
  { value: 1, label: "Strongly disagree" },
  { value: 2, label: "Disagree" },
  { value: 3, label: "Neutral" },
  { value: 4, label: "Agree" },
  { value: 5, label: "Strongly agree" },
];

const FREQUENCY_4: ReadonlyArray<AnswerOption> = [
  { value: 1, label: "Never" },
  { value: 2, label: "Once" },
  { value: 3, label: "A few times" },
  { value: 4, label: "Often" },
];

const FREQUENCY_4_FUND: ReadonlyArray<AnswerOption> = [
  { value: 1, label: "Never" },
  { value: 2, label: "Once" },
  { value: 3, label: "2–3×" },
  { value: 4, label: "Weekly+" },
];

export const QUESTION_BANK_V2: ReadonlyArray<Question> = [
  // Impulse (Spender)
  {
    id: "imp_01",
    dim: "impulse",
    type: "likert",
    weight: 1.0,
    text: "I sometimes buy something to feel better about my day.",
    answers: LIKERT,
  },
  {
    id: "imp_03",
    dim: "impulse",
    type: "likert",
    weight: 1.0,
    text: "My weekend spending often surprises me on Monday.",
    answers: LIKERT,
  },
  {
    id: "imp_04",
    dim: "impulse",
    type: "scenario",
    weight: 1.5,
    text: "An unexpected $200 lands in your account. What's your first instinct?",
    answers: [
      { value: 1, label: "Move it to savings and forget it" },
      { value: 2, label: "Pay down something boring (bill, card)" },
      { value: 3, label: "Wait a week before deciding" },
      { value: 4, label: "Treat yourself to one nice thing" },
      { value: 5, label: "Plan a small trip or experience this month" },
    ],
  },

  // Security (Saver)
  {
    id: "sec_01",
    dim: "security",
    type: "likert",
    weight: 1.0,
    text: "A growing balance gives me more comfort than most experiences.",
    answers: LIKERT,
  },
  {
    id: "sec_02",
    dim: "security",
    type: "likert",
    weight: 1.0,
    text: "I'd rather skip a trip than break my savings rhythm.",
    answers: LIKERT,
  },
  {
    id: "sec_04",
    dim: "security",
    type: "frequency",
    weight: 1.0,
    text: "In the past month, how often did you decline a paid plan to keep savings intact?",
    answers: FREQUENCY_4,
  },

  // Scarcity (Hoarder)
  {
    id: "sca_01",
    dim: "scarcity",
    type: "likert",
    weight: 1.0,
    text: "Spending money — even on essentials — often feels like losing it.",
    answers: LIKERT,
  },
  {
    id: "sca_02",
    dim: "scarcity",
    type: "likert",
    weight: 1.0,
    text: "I keep items I rarely use because I might need them one day.",
    answers: LIKERT,
  },
  {
    id: "sca_03",
    dim: "scarcity",
    type: "likert",
    weight: 1.0,
    text: "I tend to repair things long past when replacing would cost less.",
    answers: LIKERT,
  },

  // Avoidance (Avoider)
  {
    id: "avo_01",
    dim: "avoidance",
    type: "likert",
    weight: 1.0,
    text: "I avoid opening my bank app when I expect bad news.",
    answers: LIKERT,
  },
  {
    id: "avo_02",
    dim: "avoidance",
    type: "likert",
    weight: 1.0,
    text: "Bills and statements sit unopened for days at a time.",
    answers: LIKERT,
  },
  {
    id: "avo_04",
    dim: "avoidance",
    type: "frequency",
    weight: 1.0,
    text: "In the past month, how often did you postpone a money task you'd already planned?",
    answers: FREQUENCY_4,
  },

  // Identity (Shopper)
  {
    id: "idn_01",
    dim: "identity",
    type: "likert",
    weight: 1.0,
    text: "What I buy is part of how I show up in the world.",
    answers: LIKERT,
  },
  {
    id: "idn_04",
    dim: "identity",
    type: "scenario",
    weight: 1.5,
    text: "You're about to upgrade something everyday (bag, phone, kitchen tool). What pulls you most?",
    answers: [
      { value: 1, label: "Lowest working price" },
      { value: 2, label: "Best reviews regardless of look" },
      { value: 3, label: "The one friends would recognize" },
      { value: 4, label: "Whatever matches my current aesthetic" },
      { value: 5, label: "The signature/premium tier of that category" },
    ],
  },

  // Planning (Anchor)
  {
    id: "pln_01",
    dim: "planning",
    type: "likert",
    weight: 1.0,
    text: "I plan next month's spending categories before the month starts.",
    answers: LIKERT,
  },
  {
    id: "pln_02",
    dim: "planning",
    type: "scenario",
    weight: 1.5,
    text: "A bill arrives a week earlier than expected. What's your move?",
    answers: [
      { value: 1, label: "I panic — I don't know if I can cover it." },
      { value: 2, label: "I move money around at the last minute." },
      { value: 3, label: "I pay it, then catch up later." },
      { value: 4, label: "I check my buffer and pay it without stress." },
      { value: 5, label: "It's already in my planned cashflow — paid immediately." },
    ],
  },
  {
    id: "pln_03",
    dim: "planning",
    type: "frequency",
    weight: 1.0,
    text: "In the past month, how often did you transfer money into a named fund (vacation, taxes, gifts)?",
    answers: FREQUENCY_4_FUND,
  },

  // Growth (Investor)
  {
    id: "grw_01",
    dim: "growth",
    type: "likert",
    weight: 1.0,
    text: "I'd rather earn a return that might dip than keep cash safe in a checking account.",
    answers: LIKERT,
  },
  {
    id: "grw_02",
    dim: "growth",
    type: "scenario",
    weight: 1.5,
    text: "You inherit $5,000 with no strings attached. What's your first move?",
    answers: [
      { value: 1, label: "Keep it in checking — I might need it." },
      { value: 2, label: "Pay down a small debt or bill." },
      { value: 3, label: "Split — some safety, some treats." },
      { value: 4, label: "Move most into a high-yield savings account." },
      { value: 5, label: "Invest most of it for long-term growth." },
    ],
  },
  {
    id: "grw_03",
    dim: "growth",
    type: "frequency",
    weight: 1.0,
    text: "In the past month, how often did you check or rebalance an investment account?",
    answers: FREQUENCY_4_FUND,
  },
];

export const TOTAL_QUESTIONS_V2 = QUESTION_BANK_V2.length;

/** O(1) lookup by id. Frozen so callers cannot mutate. */
export const QUESTION_BY_ID_V2: Readonly<Record<string, Question>> =
  Object.freeze(
    Object.fromEntries(QUESTION_BANK_V2.map((q) => [q.id, q])),
  );

// NOTE: the live in-quiz milestone banners come from lib/content/quiz-milestones.ts
// (afterIndex 5/10/15/19). An earlier MILESTONES_V2 array (afterIndex 1/3/7/10/14/17)
// lived here, was never wired into the player, and contradicted the live schedule —
// removed to avoid confusion (it was the only place the stale "17" count appeared).
